import { useState, useEffect } from "react";
import { Settings2, Copy, Check, Globe, Lock, Users, Trash2 } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";

interface Pond {
  id: string;
  name: string;
  type: string;
  isPublic: boolean;
  inviteCode: string | null;
  isOwner: boolean;
  parentId: string | null;
  requiresApproval: boolean;
  notifyOnJoin: boolean;
}

interface EditPondSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pond: Pond | null;
  onUpdate: (
    pondId: string,
    updates: {
      name?: string;
      isPublic?: boolean;
      requiresApproval?: boolean;
      notifyOnJoin?: boolean;
    }
  ) => Promise<boolean>;
}

export const EditPondSheet = ({
  open,
  onOpenChange,
  pond,
  onUpdate,
}: EditPondSheetProps) => {
  const [name, setName] = useState("");
  const [isPublic, setIsPublic] = useState(false);
  const [requiresApproval, setRequiresApproval] = useState(true);
  const [notifyOnJoin, setNotifyOnJoin] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Sync state with pond data
  useEffect(() => {
    if (pond) {
      setName(pond.name);
      setIsPublic(pond.isPublic);
      setRequiresApproval(pond.requiresApproval);
      setNotifyOnJoin(pond.notifyOnJoin);
    }
  }, [pond]);

  const handleSave = async () => {
    if (!pond || !name.trim()) return;

    setIsLoading(true);
    const success = await onUpdate(pond.id, {
      name: name.trim(),
      isPublic,
      requiresApproval,
      notifyOnJoin: isPublic && !requiresApproval ? notifyOnJoin : false,
    });

    setIsLoading(false);
    if (success) {
      onOpenChange(false);
    }
  };

  const copyInviteCode = () => {
    if (pond?.inviteCode) {
      navigator.clipboard.writeText(pond.inviteCode);
      setCopied(true);
      toast.success("Código copiado!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareWhatsApp = () => {
    if (!pond?.inviteCode) return;
    const message = encodeURIComponent(
      `🐸 Entre na minha lagoa "${pond.name}" no Habit Frog Tracker!\n\nUse o código: ${pond.inviteCode}`
    );
    window.open(`https://wa.me/?text=${message}`, "_blank");
  };

  if (!pond) return null;

  const isPrivatePond = pond.type === "private";

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader className="pb-4">
          <SheetTitle className="flex items-center gap-2">
            <Settings2 className="h-5 w-5" />
            Editar Lagoa
          </SheetTitle>
        </SheetHeader>

        <div className="space-y-6">
          {/* Pond Visual */}
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-pond via-pond-light to-pond-surface flex items-center justify-center shadow-lg">
              <span className="text-2xl">🐸</span>
            </div>
            <div>
              <div className="flex items-center gap-2">
                {isPrivatePond ? (
                  <Lock className="h-4 w-4 text-muted-foreground" />
                ) : isPublic ? (
                  <Globe className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <Users className="h-4 w-4 text-muted-foreground" />
                )}
                <span className="text-sm text-muted-foreground">
                  {isPrivatePond ? "Lagoa Privada" : isPublic ? "Lagoa Pública" : "Lagoa Compartilhada"}
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {isPrivatePond ? "Apenas você tem acesso" : "Membros podem ver seus hábitos"}
              </p>
            </div>
          </div>

          <Separator />

          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="pond-name">Nome da lagoa</Label>
            <Input
              id="pond-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isPrivatePond}
              placeholder="Ex: Equipe de Vendas"
            />
            {isPrivatePond && (
              <p className="text-xs text-muted-foreground">
                O nome da lagoa privada não pode ser alterado
              </p>
            )}
          </div>

          {/* Only show these options for shared ponds */}
          {!isPrivatePond && (
            <>
              {/* Invite Code */}
              {pond.inviteCode && (
                <div className="space-y-2">
                  <Label>Código de convite</Label>
                  <div className="flex gap-2">
                    <Input
                      value={pond.inviteCode}
                      readOnly
                      className="font-mono text-center tracking-widest"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={copyInviteCode}
                    >
                      {copied ? (
                        <Check className="h-4 w-4 text-primary" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  <Button
                    variant="secondary"
                    size="sm"
                    className="w-full"
                    onClick={shareWhatsApp}
                  >
                    Compartilhar via WhatsApp
                  </Button>
                </div>
              )}

              <Separator />

              {/* Visibility */}
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="public-toggle">Lagoa pública</Label>
                  <p className="text-xs text-muted-foreground">
                    Qualquer pessoa pode buscar e encontrar
                  </p>
                </div>
                <Switch
                  id="public-toggle"
                  checked={isPublic}
                  onCheckedChange={setIsPublic}
                />
              </div>

              {/* Requires Approval */}
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="approval-toggle">Requer aprovação</Label>
                  <p className="text-xs text-muted-foreground">
                    Você precisa aprovar novas entradas
                  </p>
                </div>
                <Switch
                  id="approval-toggle"
                  checked={requiresApproval}
                  onCheckedChange={setRequiresApproval}
                />
              </div>

              {/* Notify on Join */}
              {isPublic && !requiresApproval && (
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="notify-toggle">Notificar ao entrar</Label>
                    <p className="text-xs text-muted-foreground">
                      Receba notificação quando alguém entrar
                    </p>
                  </div>
                  <Switch
                    id="notify-toggle"
                    checked={notifyOnJoin}
                    onCheckedChange={setNotifyOnJoin}
                  />
                </div>
              )}
            </>
          )}

          <Separator />

          {/* Save Button */}
          <Button
            variant="coral"
            className="w-full"
            onClick={handleSave}
            disabled={isLoading || !name.trim() || (isPrivatePond && name === pond.name)}
          >
            {isLoading ? "Salvando..." : "Salvar Alterações"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};
